<template>
   <div class="funding">
      <process-header :step="7" />
      <div class="body">
         <div class="step-title">Identify Funding Source</div>
         <div class='goal' v-for="goal in goals" :key="goal.id">
            <span class='goal-title'>Goal {{ goal.id }}: {{ goal.name }}</span>
            <div v-for='objective in goal.objectives' :key='objective.id' class='objective'>
               <div class='objective-head'>
                  <span class='objective-title'>Objective: {{ objective.description }}</span>
               </div>
               <vue-editable-grid
                  :ref="`f-table${objective.id}`"
                  :id="`fo-${objective.id}`"
                  class='funding-table'
                  :column-defs="columnDefs"
                  :row-data="objective.fundings"
                  row-data-key="activity_id"
                  :enable-filters="false"
                  @cell-updated.prevent="event => cellUpdated(event, objective)"
                  :item-height="35"
               />
            </div>
         </div>
         <save-continue-btns
            :back-route="{name: 'priority-assessment'}"
            @save="save"
            @save-and-continue="saveAndContinue"
            not-for-form
         />
      </div>
   </div>
</template>

<script>
import ProcessHeader from "../Header";
import SaveContinueBtns from '@/components/molecules/Home/Plan/SaveContinueBtns';
import { fetchWithAuthToken, postWithAuthToken } from "../../../../../axios";
import { showError, showSuccess, showLoading, hideLoading, currencyToNumber, formatNumberNepaliStyle } from "../../../../../js/ui-helpers";
import { isString } from 'highcharts';

export default {
   name: "funding",
   components: {
      ProcessHeader,
      SaveContinueBtns,
   },
   data()
   {
      return {
         goals: [],
         fundAmtFields: [],
         postFields: [],
         columnDefs: [],
      };
   },
   computed: {
      planId()
      {
         return this.$route.params.id;
      },
   },
   created()
   {
      fetchWithAuthToken(`api/plan/${this.planId}/fs/`)
      .then(({data: sources}) =>
      {
         this.columnDefs = this.getColumnDefs(sources);
         this.fundAmtFields = sources.map(src=>src.code);
         this.postFields = [...this.fundAmtFields, 'activity_id']
      });
      fetchWithAuthToken(`api/plan/${this.planId}/fundings/`)
      .then( ({data: goals}) =>
      {
         const getFunding = activity => ({ ...activity,...activity.fundings });
         const formatObjective = objective => ({ ...objective,fundings: objective.activities.map(getFunding) });

         this.goals = goals.map(goal => ({
            ...goal,
            objectives: goal.objectives.map(formatObjective),
         }));
      });
  },
  methods: {
      getColumnDefs(sources)
      {
          
         const numericFormatter = event => {
            if (event.reverse)
            {
               return currencyToNumber(event.value);
            }
            return formatNumberNepaliStyle(event.value, {maxPrecision: 2});
         }
         const fundAmtColumns = sources.map(source => ({
            field: source.code,
            headerName: source.name,
            sortable: true,
            step: 0.01,
            formatter: numericFormatter,
            type: 'numeric',
            size: '12ch',
            editable: true,
         }));
         const totalColumn = {
            field: "total",
            headerName: "Total",
            formatter: numericFormatter,
            sortable: true,
            type: "numeric",
            size: '15ch',
         };
         return [
            { field: "activity_description", headerName: "Activity", sortable: true, size: 'minmax(30ch, 1fr)', },
            { field: "activity_code", headerName: "Code", sortable: true, size: '9ch' },
            ...fundAmtColumns,
            totalColumn
         ];
      },
      cellUpdated($event)
      {
         const { column,row,value } = $event;
         if (value < 0 || isString(value))
         {
            return showError(
               "Funding",
               `${column.headerName} can't be negative or empty.`
            );
         }
         row[column.field] = Number(value);
         row.total = this.fundAmtFields.reduce( ( total,field ) =>{
            return total+Number(row[field])
         } , 0);
      },
      getFundingFromRow(row)
      {
         return this.postFields.reduce((acc, field) => ({...acc, [field]: row[field]}), {});
      },
      getPostFundingData()
      {
         return this.goals.flatMap(goal => goal.objectives)
            .flatMap(obj => obj.fundings )
            .map(this.getFundingFromRow);
      },
      save({goToNext=false}={})
      {
         const data = this.getPostFundingData();
         console.log(data);
         showLoading()
         postWithAuthToken(`api/plan/${this.planId}/fundings/`, data)
         .then(() =>
         {
            showSuccess('Funding', 'Successfully saved');
            if (goToNext)
            {
               this.$router.push({name: "rankings"});
            }
         })
         .catch(() => showError('Funding', 'Failed to save data'))
         .finally(() => hideLoading());
      },
      saveAndContinue()
      {
         this.save({goToNext: true});
      }
   },
};
</script>

<style lang="scss" scoped>
.funding {
   font-family: $primary-font;
   color: $font-color;
   .step-title {
      font-family: $secondary-font;
      font-weight: 500;
      font-size: 1.05em;
      margin-bottom: 0.5em;
   }
   > .body {
      padding: 0.6em;
      .goal {
         background: $secondary-bg;
         padding: 0.5em;
         margin-top: 1em;
         border-radius: 5px;
         .goal-title {
            padding: 0.3em 0.2em;
            font-weight: 600;
            margin-bottom: 0.3em;
         }
         .objective {
            padding: 0.2em;
            &:not(:last-child) {
               margin-bottom: .7em;
            }
            border-radius: 5px;
            background: white;
            .objective-head {
               .objective-title {
                  font-weight: 600;
                  padding: 0.5em 0.2em;
               }
               display: flex;
               justify-content: space-between;
            }
         }
      }
   }
   .funding-table {
      // min-height: 2em;
      font-size: 0.9em;
   }
   .warning {
      padding: 4px 2px;
      background: $warning-color;
      border-radius: 2px;
      // color: white;
      font-size: 10px;
   }
   .save-btn {
      background: $primary-color;
      color: white;
      border-radius: 16px;
      padding: 8px 12px;
      margin-left: 1em;
      margin-bottom: 0.7em;
   }

   .btn-group {
      display: flex;
      margin: 0 0.3em;
      > * {
         margin-right: 0.3em;
         a {
         color: inherit;
         }
         &:last-child {
         margin-right: 0;
         }
      }
   }
}
</style>
